import { useCallback, useEffect, useRef, useState } from 'react';
import type { StepAnswer, TestNote, TestReport } from '../api/tests';
import { ca } from '../i18n/ca';

/** How long after the last key a draft is sent: short enough that the others read it as it is written. */
export const AUTOSAVE_MS = 700;

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/**
 * A text box that saves itself (the owner, 2026-09-30: nothing on the test's
 * page waits for a button). The draft is sent a moment after the last key and
 * at once when the box is left. What the platform holds replaces the draft
 * only while nobody is writing in it: the page reads the test again every
 * time somebody else writes, and a box that took that answer mid-sentence
 * would eat the sentence.
 */
function useAutosave(saved: string, onSave: (text: string) => Promise<unknown>) {
  const [draft, setDraft] = useState(saved);
  const [state, setState] = useState<SaveState>('idle');
  const dirty = useRef(false);
  const focused = useRef(false);
  const timer = useRef<number | null>(null);
  const latest = useRef(saved);

  useEffect(() => {
    if (!dirty.current && !focused.current) setDraft(saved);
  }, [saved]);

  const send = useCallback(
    (text: string) => {
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
        timer.current = null;
      }
      if (!dirty.current) return;
      dirty.current = false;
      setState('saving');
      onSave(text)
        .then(() => setState(latest.current === text ? 'saved' : 'saving'))
        .catch(() => {
          dirty.current = true;
          setState('error');
        });
    },
    [onSave],
  );

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const change = (text: string) => {
    setDraft(text);
    latest.current = text;
    dirty.current = true;
    setState('idle');
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => send(latest.current), AUTOSAVE_MS);
  };

  return {
    draft,
    state,
    change,
    focus: () => {
      focused.current = true;
    },
    blur: () => {
      focused.current = false;
      send(latest.current);
    },
  };
}

function SaveLine({ state }: { state: SaveState }) {
  const t = ca.testsPage;
  if (state === 'idle') return null;
  return (
    <span className={`save-state small ${state === 'error' ? 'error' : 'muted'}`} role="status">
      {state === 'saving' ? t.saving : state === 'saved' ? t.saved : t.saveError}
    </span>
  );
}

/**
 * A person's own answer and comment on a step. The answer is sent when it is
 * chosen and the comment as it is written; both are this person's alone, so
 * nobody's page writes over anybody else's word.
 */
export function StepEditor({
  current,
  note,
  onSave,
}: {
  current: string | null;
  note: string | null;
  onSave: (body: { answer?: StepAnswer; note?: string }) => Promise<unknown>;
}) {
  const t = ca.testsPage;
  const [answerState, setAnswerState] = useState<SaveState>('idle');
  const saveNote = useCallback((text: string) => onSave({ note: text }), [onSave]);
  const box = useAutosave(note ?? '', saveNote);
  const choices: [StepAnswer, string][] = [
    ['yes', t.fixYes],
    ['no', t.fixNo],
    ['cant', t.fixCant],
  ];
  return (
    <div className="step-edit">
      <div className="step-answers" role="group" aria-label={t.fixLabel}>
        {choices.map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={`small answer-${value}`}
            aria-pressed={current === value}
            onClick={() => {
              setAnswerState('saving');
              onSave({ answer: value })
                .then(() => setAnswerState('saved'))
                .catch(() => setAnswerState('error'));
            }}
          >
            {label}
          </button>
        ))}
        <SaveLine state={box.state === 'idle' ? answerState : box.state} />
      </div>
      <label className="step-note-edit">
        <span className="muted small">{t.stepNote}</span>
        <textarea
          value={box.draft}
          onChange={(e) => box.change(e.target.value)}
          onFocus={box.focus}
          onBlur={box.blur}
          placeholder={t.stepNotePlaceholder}
          maxLength={2000}
          rows={2}
        />
      </label>
    </div>
  );
}

/** A person's own comment on the test as a whole, saved as it is written, above everybody else's. */
export function CardNotes({
  notes,
  canWrite,
  onSave,
}: {
  notes: TestNote[];
  canWrite: boolean;
  onSave: (text: string) => Promise<unknown>;
}) {
  const t = ca.testsPage;
  const mine = notes.find((n) => n.mine);
  const others = notes.filter((n) => !n.mine);
  const box = useAutosave(mine?.note ?? '', onSave);
  return (
    <div className="card-notes">
      {canWrite && (
        <label className="note">
          {t.noteLabel} <SaveLine state={box.state} />
          <textarea
            value={box.draft}
            onChange={(e) => box.change(e.target.value)}
            onFocus={box.focus}
            onBlur={box.blur}
            placeholder={t.notePlaceholder}
            maxLength={2000}
            rows={3}
          />
        </label>
      )}
      {!canWrite && mine && (
        <p className="step-note">
          <strong>{mine.by}</strong> {mine.note}
        </p>
      )}
      {others.length > 0 && (
        <ul className="activity" aria-label={t.othersNotes}>
          {others.map((n) => (
            <li key={n.by + n.at}>
              <strong>{n.by}</strong> {n.note}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const minute = (seconds: number) => Math.max(0, Math.round(seconds / 60));

/** What the players reported from the game with F9, everybody's, each with the picture its watcher took. */
export function GameReports({ reports }: { reports: TestReport[] }) {
  const t = ca.testsPage;
  const said = reports.filter((r) => r.why === 'report');
  if (said.length === 0) return null;
  return (
    <>
      <h3>{t.gameReports}</h3>
      <p className="muted small">{t.gameReportsHint}</p>
      <ul className="game-reports" aria-label={t.gameReports}>
        {said.map((r) => (
          <li key={r.id + r.player}>
            {r.thumbUrl && r.url ? (
              <a href={r.url} target="_blank" rel="noreferrer" className="thumb">
                <img src={r.thumbUrl} alt={t.reportPicture(r.by)} loading="lazy" />
              </a>
            ) : (
              <span className="thumb none muted small">{t.noPicture}</span>
            )}
            <span>
              <strong>{r.by}</strong>{' '}
              <span className="muted small">
                {t.reportAt(minute(r.at))}
                {r.grid ? ` · ${r.grid}` : ''}
              </span>
              <br />
              {r.note || <span className="muted">{t.reportNoNote}</span>}
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}
